import React, { useState, useRef, useEffect } from "react";
import {
  type GridMetricKey,
  type MetricScale,
  METRIC_LABELS,
  ENDPOINT_METRICS,
  MIDPOINT_METRICS,
} from "../../../api/grid";

interface MetricToggleProps {
  selected: GridMetricKey;
  onChange: (metric: GridMetricKey) => void;
  scale?: MetricScale;
  onScaleChange?: (scale: MetricScale) => void;
}

const MetricToggle: React.FC<MetricToggleProps> = ({ selected, onChange, scale, onScaleChange }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isEndpoint = ENDPOINT_METRICS.some((m) => m.key === selected);

  return (
    <div className="flex items-center gap-2">
      {/* Scale toggle */}
      {scale && onScaleChange && (
        <div className="flex rounded-lg bg-surface-800/90 backdrop-blur-sm border border-border/50 overflow-hidden">
          {(["intensity", "annual"] as MetricScale[]).map((s) => (
            <button
              key={s}
              onClick={() => onScaleChange(s)}
              className={`px-2.5 py-1.5 text-body-sm font-medium transition-colors whitespace-nowrap ${
                scale === s
                  ? "bg-quantum-500/20 text-quantum-400 border-b-2 border-quantum-500"
                  : "text-text-secondary hover:text-text-primary hover:bg-surface-700"
              }`}
            >
              {s === "intensity" ? "Per MWh" : "Annual"}
            </button>
          ))}
        </div>
      )}

      {/* Endpoint quick-pick buttons */}
      <div className="flex rounded-lg bg-surface-800/90 backdrop-blur-sm border border-border/50 overflow-hidden">
        {ENDPOINT_METRICS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { onChange(key); setOpen(false); }}
            className={`px-3 py-1.5 text-body-sm font-medium transition-colors whitespace-nowrap ${
              selected === key
                ? "bg-quantum-500/20 text-quantum-400 border-b-2 border-quantum-500"
                : "text-text-secondary hover:text-text-primary hover:bg-surface-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Midpoint dropdown */}
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setOpen(!open)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-body-sm font-medium transition-colors border ${
            !isEndpoint
              ? "bg-quantum-500/20 text-quantum-400 border-quantum-500"
              : "bg-surface-800/90 text-text-secondary hover:text-text-primary border-border/50"
          }`}
        >
          {!isEndpoint ? METRIC_LABELS[selected] : "More metrics"}
          <svg
            className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-1 w-64 bg-surface-800 border border-border rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
            <div className="px-3 py-2 text-caption text-text-muted font-semibold uppercase tracking-wider border-b border-border/50">
              ReCiPe 2016 Midpoints
            </div>
            {MIDPOINT_METRICS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { onChange(key); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-body-sm transition-colors ${
                  selected === key
                    ? "bg-quantum-500/15 text-quantum-400"
                    : "text-text-secondary hover:bg-surface-700 hover:text-text-primary"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricToggle;
